import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Teardrop pin as an inline divIcon → no image assets to break under CRA.
const pinIcon = (color = '#4F46E5') => L.divIcon({
  className: 'twp-pin',
  html: `<div style="width:18px;height:18px;border-radius:50% 50% 50% 0;background:${color};transform:rotate(-45deg);border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.45)"></div>`,
  iconSize: [18, 18], iconAnchor: [9, 18], popupAnchor: [0, -16],
});

/**
 * Reusable OpenStreetMap (free, no API key) map built on plain Leaflet so it
 * works with any React version.
 *
 * props:
 *  center [lat,lng], zoom
 *  markers [{lat,lng,color,label,popup,draggable,onDragEnd(lat,lng)}]
 *  routeLine [[lat,lng],...] | null   (dashed planned line)
 *  track [[lat,lng],...] | null       (solid line of the GPS route actually driven)
 *  circles [{lat,lng,radius,color}] | null (e.g. geofence radius)
 *  onMapClick(lat,lng)
 *  height (css string, default 320px)  fit (bool, fit to markers)
 */
export const LeafletMap = ({
  center = [11.6643, 78.1460], zoom = 11, markers = [], routeLine = null, track = null, circles = null,
  onMapClick, height = '320px', fit = true, className = '', testId = 'leaflet-map',
}) => {
  const elRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const clickRef = useRef(onMapClick);
  clickRef.current = onMapClick;

  // init once
  useEffect(() => {
    if (mapRef.current || !elRef.current) return;
    const map = L.map(elRef.current, { center, zoom, scrollWheelZoom: true });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap contributors',
    }).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    map.on('click', (e) => { if (clickRef.current) clickRef.current(e.latlng.lat, e.latlng.lng); });
    mapRef.current = map;
    setTimeout(() => map.invalidateSize(), 150);
    return () => { map.remove(); mapRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // redraw markers + route when they change
  useEffect(() => {
    const map = mapRef.current, layer = layerRef.current;
    if (!map || !layer) return;
    layer.clearLayers();
    const pts = [];
    (markers || []).forEach((m) => {
      if (m.lat == null || m.lng == null) return;
      const mk = L.marker([m.lat, m.lng], { icon: pinIcon(m.color), draggable: !!m.draggable });
      if (m.popup || m.label) mk.bindPopup(m.popup || m.label);
      if (m.draggable && m.onDragEnd) mk.on('dragend', (e) => { const p = e.target.getLatLng(); m.onDragEnd(p.lat, p.lng); });
      mk.addTo(layer);
      pts.push([m.lat, m.lng]);
    });
    (circles || []).forEach((c) => {
      if (c.lat == null || c.lng == null) return;
      L.circle([c.lat, c.lng], { radius: c.radius, color: c.color || '#F59E0B', weight: 1.5, fillOpacity: 0.12 }).addTo(layer);
    });
    if (routeLine && routeLine.length >= 2) {
      L.polyline(routeLine, { color: '#1A1F2E', weight: 3, dashArray: '6 6', opacity: 0.7 }).addTo(layer);
    }
    if (track && track.length >= 2) {
      L.polyline(track, { color: '#0D9488', weight: 4, opacity: 0.85 }).addTo(layer);
      pts.push(...track);
    }
    if (fit && pts.length >= 2) {
      try { map.fitBounds(L.latLngBounds(pts).pad(0.3)); } catch { /* ignore */ }
    } else if (pts.length === 1) {
      map.setView(pts[0], Math.max(map.getZoom(), 13));
    }
  }, [markers, routeLine, track, circles, fit]);

  // recenter when center prop changes and there are no markers to fit
  useEffect(() => {
    const map = mapRef.current;
    if (map && (!markers || markers.length === 0) && center) map.setView(center, zoom);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [center?.[0], center?.[1]]);

  return <div ref={elRef} data-testid={testId} className={`rounded-xl overflow-hidden border border-black/10 ${className}`} style={{ height, width: '100%', zIndex: 0 }} />;
};

export default LeafletMap;
